import { useEffect, useRef, useState } from "react";
import { paper, Point, Size, Rectangle, Path, Segment, PointText } from "paper";
import Footer from "../../layout/Footer";
import Header from "../../layout/Header";
import Debug from "./Debug";
import { Button, Modal, Form } from "react-bootstrap";
import { colors } from "../../../helpers/colors";
import {
  TRUESPACE_LABEL,
  openTrueSpace,
  projectPointFromEvent,
  hitsLink,
} from "../../shared/trueSpace";
import { computeAsf } from "../../shared/asf";
import { correctApparentDip } from "../../shared/inclination";
import { useToasts } from "react-toast-notifications";
import DownloadMenu from "../../shared/DownloadMenu";
import { MULTIPLIER, PADDING, POINTS_OFFSET, SCALE_INTERVAL } from "../utils/constants";
import { toStartOffset, scaleLabelChainage, chainageToCanvasX } from "../../shared/chainage";
import { toRadians, toDegree } from "../utils/mathUtils";
import { calculateAlpha, calculateQuadrants, calculateDopp, calculateApDir } from "../utils/calculations";
import { FLAT_INCLINED_ALERT } from "../utils/horizontalTrace";
import {
  alphaBetween,
  solveAlphaZero,
  solveLevel,
  solveVertical,
  solveParallelTrace,
  alphaZeroInclined,
  transverseSign,
  crossSectionPoint,
  polylineExtent,
  polylineLength,
  perimeterYlog,
  BETA_ZERO_TOLERANCE,
  ALPHA0_INVERT_ALERT,
  ALPHA0_PARALLEL_NOTE,
  coincidenceAlert,
  FLAT_PARALLEL_NOTE,
  AXIS_PARALLEL_NOTE,
  VERTICAL_CENTRELINE_ALERT,
  INVISIBLE_INVERT_ALERT,
  INVISIBLE_SPL_ALERT,
  INVISIBLE_CROWN_ALERT,
} from "../../shared/alphaZero";
import { createTraceAlerts, changedTraces, CLIP_ALERT, drawnPastLog } from "../../shared/traceAlerts";
import { labelCell } from "../../shared/labelField";
import { faceLogPointRow } from "../../shared/faceLogRows";
import { faceLogSecondPoint } from "../../shared/orientationSources";
import { drawTunnelHeader } from "../../shared/tunnelHeader";
import { chainageExtent, fullLogExtent, NO_EXTENT } from "../../shared/traceExtent";

const LOG_LINE_ALERTS = [INVISIBLE_INVERT_ALERT, INVISIBLE_SPL_ALERT, INVISIBLE_CROWN_ALERT];

/**
 * Output Component for Horseshoe Tunnel
 * Handles trace visualization and all mathematical calculations
 * Implements P6 (Any Other Point) calculation algorithm
 */
const Output = (props) => {
  let { addToast, removeAllToasts } = useToasts();
  const [uservariables, setUserVariables] = useState([]);
  const [show, setShow] = useState(false);
  const [selectedTrace, setSelectedTrace] = useState(false);
  const handleClose = () => setShow(false);

  // Common variables
  let tracedimensions = props.dimensions[0];
  let multiplier = MULTIPLIER;
  let padding = PADDING;
  let points = POINTS_OFFSET;
  let length = (tracedimensions.endrd - tracedimensions.startrd) * multiplier;
  // Chainage origin: x=padding represents the Start Chainage, not 0. Traces are
  // anchored relative to this offset and the scale prints absolute chainages.
  let startOffsetRD = toStartOffset(tracedimensions.startrd);
  let height = tracedimensions.height * multiplier;
  let width = tracedimensions.width * multiplier;
  let hw = tracedimensions.hw * multiplier;
  let ws = tracedimensions.widthspl * multiplier;
  let scalearr = [];
  let text;
  let i = 0;
  let Whalf = ws / 2;
  let HC = height - hw;
  let r = (Math.pow(Whalf, 2) + Math.pow(HC, 2)) / (2 * HC);
  let Theta = toDegree(Math.atan((r - HC) / Whalf));
  let Arc = (Math.PI * r) / 2 - ((Math.PI * r) / 2) * (Theta / 90);
  let ArcFull = Arc * 2;
  let HWn = height - r;
  let HWincl = Math.sqrt(Math.pow(hw, 2) + Math.pow((ws - width) / 2, 2));
  let Ymax = padding + 2 * (HWincl + Arc);
  let usrvar = [];
  let legendpath = {};

  const updateUserVariable = (usrvar) => {
    setUserVariables(usrvar);
  };

  // Box of the TrueSpace link drawn in the output notes, in project
  // coordinates. Set while drawing; read by the canvas mouse handlers below.
  const trueSpaceLinkBounds = useRef(null);

  const canvasProjectPoint = (e) => {
    const view = paper.view;
    if (!view) return null;
    return projectPointFromEvent(
      e,
      e.currentTarget.getBoundingClientRect(),
      view.viewSize.width
    );
  };

  const handleCanvasClick = (e) => {
    if (hitsLink(canvasProjectPoint(e), trueSpaceLinkBounds.current)) {
      openTrueSpace();
    }
  };

  const handleCanvasMouseMove = (e) => {
    const over = hitsLink(canvasProjectPoint(e), trueSpaceLinkBounds.current);
    e.currentTarget.style.cursor = over ? "pointer" : "default";
  };

  useEffect(() => {
    if (!props.showdebug) {
      paper.setup("paper-canvas");
      creategraph();
    }
  }, [props.dimensions, props.showdebug]);

  const createScale = () => {
    if (tracedimensions.iscrown) {
      Ymax = padding + 2 * Arc + HWincl;
    }
    for (i = padding; i <= length + padding; i += multiplier * SCALE_INTERVAL) {
      text = new PointText(new Point(i, Ymax + 15));
      text.justification = "center";
      text.fillColor = "black";
      text.content = scaleLabelChainage(i, padding, multiplier, startOffsetRD);
      scalearr.push([i, Ymax + 10]);
      scalearr.push([i, Ymax]);
      if (i + multiplier * SCALE_INTERVAL <= length + padding) {
        for (let j = 1; j <= 9; j++) {
          scalearr.push([i + multiplier * j, Ymax]);
          scalearr.push([i + multiplier * j, Ymax + 5]);
          scalearr.push([i + multiplier * j, Ymax]);
        }
        scalearr.push([i + multiplier * SCALE_INTERVAL, Ymax]);
      }
    }
    new Path({
      segments: scalearr,
      strokeColor: "black",
      strokeWidth: 1,
    });
    if (tracedimensions.iscrown) {
      Ymax = padding + 2 * (HWincl + Arc);
    }
  };

  const createLog = () => {
    let style = {
      fillColor: "white",
      strokeColor: "black",
      strokeWidth: 1,
    };
    let path, path1;
    if (!tracedimensions.iscrown) {
      path = new Path.Rectangle(new Rectangle(new Point(padding, padding), new Size(length, 2 * (HWincl + Arc))));
      path.style = style;
      path1 = new Path.Rectangle(new Rectangle(new Point(padding, padding + HWincl), new Size(length, 2 * Arc)));
      path1.style = style;
    } else {
      path = new Path.Rectangle(new Rectangle(new Point(padding, padding + HWincl), new Size(length, 2 * Arc)));
      path.style = style;
    }

    var crpoint1 = path.bounds.rightCenter.add([HC, -(ws / 2)]);
    var crpoint2 = crpoint1.add([0, ws]);
    var crpoint3 = crpoint2.add([hw, (width - ws) / 2]);
    var crpoint4 = crpoint3.add([0, -width]);
    var path2 = new Path({
      segments: [new Segment(crpoint1), new Segment(crpoint2), new Segment(crpoint3), new Segment(crpoint4)],
    });
    path2.closed = true;
    path2.style = style;
    legendpath["path2"] = path2;

    var path21 = new Path.Arc(crpoint1, path.bounds.rightCenter, crpoint2);
    path21.style = style;

    var path3 = new Path.Line(path.bounds.leftCenter, path2.bounds.rightCenter);
    path3.strokeColor = "black";
    path3.dashArray = [4, 10];


    if (!tracedimensions.iscrown) {
      var path4 = new Path(new Segment(path.bounds.topRight), new Segment(crpoint4));
      path4.strokeColor = "black";
      path4.dashArray = [14, 5];
      var path5 = new Path(new Segment(path.bounds.bottomRight), new Segment(crpoint3));
      path5.strokeColor = "black";
      path5.dashArray = [14, 5];
      var path6 = new Path(new Segment(path1.bounds.topRight), new Segment(path2.bounds.topLeft));
      path6.strokeColor = "black";
      path6.dashArray = [14, 5];
      var path7 = new Path(new Segment(path1.bounds.bottomRight), new Segment(path2.bounds.bottomLeft));
      path7.strokeColor = "black";
      path7.dashArray = [14, 5];
    } else {
      var path4 = new Path(new Segment(path.bounds.topRight), new Segment(path2.bounds.topLeft));
      path4.strokeColor = "black";
      path4.dashArray = [3, 5];
      var path5 = new Path(new Segment(path.bounds.bottomRight), new Segment(path2.bounds.bottomLeft));
      path5.strokeColor = "black";
      path5.dashArray = [3, 5];
    }

    if (!tracedimensions.iscrown) {
      text = new PointText(path.bounds.topLeft.add([-5, 5]));
      text.justification = "right";
      text.fillColor = "black";
      text.content = "Left Invert";
      text = new PointText(path.bounds.bottomLeft.add([-5, 5]));
      text.justification = "right";
      text.fillColor = "black";
      text.content = "Right Invert";
      text = new PointText(path1.bounds.topLeft.add([-5, 5]));
      text.justification = "right";
      text.fillColor = "black";
      text.content = "Left Spring Level";
      text = new PointText(path1.bounds.bottomLeft.add([-5, 5]));
      text.justification = "right";
      text.fillColor = "black";
      text.content = "Right Spring Level";
    } else {
      text = new PointText(path.bounds.topLeft.add([-5, 5]));
      text.justification = "right";
      text.fillColor = "black";
      text.content = "Left Spring Level";
      text = new PointText(path.bounds.bottomLeft.add([-5, 5]));
      text.justification = "right";
      text.fillColor = "black";
      text.content = "Right Spring Level";
    }
    text = new PointText(path.bounds.leftCenter.add([-5, 5]));
    text.justification = "right";
    text.fillColor = "black";
    text.content = "Center Line";
    drawTunnelHeader({ Path, Point, PointText, bounds: path.bounds, direction: tracedimensions.direction, inclination: tracedimensions.inclination, style });
  };

  /**
   * Draw P6 point on tunnel face/heading with tunnel face line
   * @param {Object} dimension - Trace dimension object
   * @param {number} index - Trace index for color
   * @param {Object} style - Style object for the trace
   * @param {Array} discontinuityInputPoints - Array to store discontinuity input points
   */
  const drawP6Point = (dimension, index, style, discontinuityInputPoints, labelLift = 0) => {
    const orientation = dimension.orientation || "";
    if (orientation !== "otherpoint") return;

    // Calculate Y coordinates for the tunnel log
    const P1y = tracedimensions.iscrown ? padding + HWincl : padding;
    const P3y = tracedimensions.iscrown ? padding + HWincl + ArcFull : padding + 2 * HWincl + ArcFull;
    const rdxcoordinate = chainageToCanvasX(dimension.rd, padding, multiplier, startOffsetRD);

    // Draw dotted vertical line at RD (tunnel face line)
    const rdLine = new Path.Line(
      new Point(rdxcoordinate, P1y - 7),
      new Point(rdxcoordinate, P3y)
    );
    rdLine.style = { ...style, dashArray: [4, 4] };
    rdLine.strokeColor = colors[index];
    rdLine.strokeWidth = 1;

    // Draw "Tunnel Face" text
    // Two faces a few metres apart would print their labels over each other.
    const tunnelFaceText = new PointText(new Point(rdxcoordinate - 5, P1y - 10 - labelLift));
    tunnelFaceText.content = "Tunnel Face";
    tunnelFaceText.style = { fontSize: 8 };
    tunnelFaceText.fillColor = colors[index];
    tunnelFaceText.justification = "center";

    // Draw arrow pointing down
    const arrowDown = new Path.Line(
      new Point(rdxcoordinate, P1y - 5),
      new Point(rdxcoordinate, P1y - 2)
    );
    arrowDown.style = style;
    arrowDown.strokeColor = colors[index];
    arrowDown.strokeWidth = 1;

    // Draw arrow left wing
    const arrowLeft = new Path.Line(
      new Point(rdxcoordinate, P1y),
      new Point(rdxcoordinate - 3, P1y - 3)
    );
    arrowLeft.style = style;
    arrowLeft.strokeColor = colors[index];
    arrowLeft.strokeWidth = 1;

    // Draw arrow right wing
    const arrowRight = new Path.Line(
      new Point(rdxcoordinate, P1y),
      new Point(rdxcoordinate + 3, P1y - 3)
    );
    arrowRight.style = style;
    arrowRight.strokeColor = colors[index];
    arrowRight.strokeWidth = 1;

    // Draw P6 point on tunnel face legend
    // Reference point: intersection of center line and Invert (bottom of tunnel)
    if (legendpath["path2"]) {
      // Get actual segment points from path2 (the quadrilateral)
      // segments[0] = crpoint1 (SPL level, left wall - top of left edge)
      // segments[1] = crpoint2 (SPL level, right wall - bottom of left edge)
      // segments[2] = crpoint3 (Invert level, right side)
      // segments[3] = crpoint4 (Invert level, left side)
      const crpoint1 = legendpath["path2"].segments[0].point;
      const crpoint2 = legendpath["path2"].segments[1].point;
      const crpoint3 = legendpath["path2"].segments[2].point;

      // Reference point: the invert (x = crpoint3.x) ∩ the centre line
      // (midpoint between the SPL corners). Height above the invert runs
      // towards the crown (−x), the offset towards the named side.
      const refX = crpoint3.x;
      const refY = (crpoint1.y + crpoint2.y) / 2;

      const Hp = parseFloat(dimension.verticalheight) || 0;
      const d = parseFloat(dimension.distancefromcenter) || 0;
      const yOffset = (dimension.tunnelside === "left" ? -d : d) * multiplier;

      let path2center = new Point(refX - Hp * multiplier, refY + yOffset);

      const tunnelDotOnLegend = new Path.Circle(path2center, 2);
      tunnelDotOnLegend.style = { ...style, fillColor: colors[index] };

      discontinuityInputPoints.push({
        point: path2center,
        style: style,
        color: colors[index]
      });
    }
  };

  const createTrace = () => {
    const discontinuityInputPoints = [];
    let fixedTextPosition = null;
    if (legendpath["path2"]) {
      const path2Center = legendpath["path2"].bounds.center;
      fixedTextPosition = new Point(path2Center.x + 30, path2Center.y - 30);
    }

    usrvar["Length"] = length / multiplier;
    usrvar["Height"] = height / multiplier;
    usrvar["Width"] = width / multiplier;
    usrvar["HW"] = hw / multiplier;
    usrvar["WS"] = ws / multiplier;
    usrvar["Whalf"] = Whalf / multiplier;
    usrvar["HC"] = HC / multiplier;
    usrvar["HWincl"] = HWincl / multiplier;
    usrvar["R"] = r / multiplier;
    usrvar["Theta"] = Theta;
    usrvar["Arc"] = Arc / multiplier;
    usrvar["ArcFull"] = ArcFull / multiplier;

    const orientationLocationMap = {
      leftinvert: "Left Invert",
      leftspl: "Left SPL",
      rightinvert: "Right Invert",
      rightspl: "Right SPL",
      centerline: "Centreline",
      otherpoint: "",
    };
    const tunnelSideLabelMap = { left: "Left", right: "Right", center: "Center" };
    const inputRows = [];
    const outputRows = [];
    // Distinct alerts collected over all discontinuities; raised once each
    // after the loop and printed under the Outputs table.
    const horizontalAlerts = createTraceAlerts();
    const clipAlerts = createTraceAlerts();

    // The module-level geometry is in canvas units; the cross-section solvers
    // in shared/alphaZero work in metres. HWincl is the slant wall length the
    // unfolded log uses for each wall panel.
    const geomM = {
      W: width / multiplier,
      WS: ws / multiplier,
      HW: hw / multiplier,
      HWincl: HWincl / multiplier,
      H: height / multiplier,
      R: r / multiplier,
      HWn: HWn / multiplier,
      Arc: Arc / multiplier,
      ArcFull: ArcFull / multiplier,
      Theta,
    };
    const logStartRD = startOffsetRD;
    const logEndRD = Number(tracedimensions.endrd);
    const fmt1 = (v) => Number(v).toFixed(1).replace(/\.0$/, "");

    // "Any Other Point" is a point on the face: side of the centreline,
    // offset d and height Hp above the invert (Algorithm Navigator, P6).
    const faceP6 = (dimension) => ({
      side: dimension.tunnelside,
      d: dimension.distancefromcenter,
      Hp: dimension.verticalheight,
    });

    // d = 0 is the centreline whichever side was named.
    const tunnelSideLabel = (dimension) =>
      parseFloat(dimension.distancefromcenter) === 0
        ? tunnelSideLabelMap.center
        : tunnelSideLabelMap[dimension.tunnelside];
    // A trace whose orientation came from known chainages has no face-heading
    // point to report; the three chainages the user entered are the useful
    // record, so they take those columns and the sub-group is relabelled.
    const isChainageSource = (dimension) =>
      (dimension.orientationsource || "known") === "chainages";
    const chainageCell = (v) =>
      v === undefined || v === null || v === "" ? "-" : String(v);

    // One Inputs-table row per trace, the same for every plotting branch.
    // Tunnel-surface rows show their location name; only Face (Heading) rows
    // ("Any Other Point") carry side / distance / height data.
    const buildInputRow = (dimension, index, DirInput, amtInput) => {
      const traceOrientation = dimension.orientation || "leftinvert";
      const isP6 = traceOrientation === "otherpoint";
      const p6Cell = (v) => (isP6 && v !== undefined && v !== "" ? String(v) : "-");
      const row = {
        no: String(index + 1),
        label: labelCell(dimension),
        dir: String(DirInput),
        amt: String(amtInput),
        ch: String(dimension.rd),
        surface: orientationLocationMap[traceOrientation] || "-",
        side: isP6 ? tunnelSideLabel(dimension) || "-" : "-",
        dist: p6Cell(dimension.distancefromcenter),
        ht: p6Cell(dimension.verticalheight),
        color: colors[index],
        ...(isChainageSource(dimension)
          ? {
              source: "chainages",
              side: chainageCell(dimension.p4),
              dist: chainageCell(dimension.p2),
              ht: chainageCell(dimension.p5),
            }
          : { source: dimension.orientationsource || "known" }),
      };
      // Face logs carry a known point on each face; both are inputs.
      if ((dimension.orientationsource || "known") !== "facelogs") return [row];
      return [
        { ...row, ...faceLogPointRow(dimension, 1) },
        // The second line belongs to the same discontinuity: no repeat of its
        // number, label or orientation.
        {
          no: " ",
          label: " ",
          dir: " ",
          amt: " ",
          ...faceLogPointRow(dimension, 2),
          color: colors[index],
          source: "facelogs",
        },
      ];
    };

    // One Outputs-table row per trace; a chainage is "-" where the trace has
    // no single crossing of that line.
    const oneDecimal = (value) => {
      const n = Number(value);
      return value === "-" || value === undefined || !isFinite(n) ? "-" : n.toFixed(1);
    };
    const pushOutputRow = (index, row) => {
      outputRows.push({
        no: String(index + 1),
        angle: fmt1(row.alpha),
        adip: fmt1(row.adip),
        adipi: fmt1(row.adipi),
        chli: oneDecimal(row.P3x),
        chri: oneDecimal(row.P1x),
        chlspl: oneDecimal(row.P4x),
        chrspl: oneDecimal(row.P2x),
        chcl: oneDecimal(row.P5x),
        tsp: String(row.span),
        ...(row.extent || NO_EXTENT),
        asf: Number(row.asf).toFixed(2).replace(/\.?0+$/, ""),
        color: colors[index],
      });
    };

    // Clip a drawn trace to the log's chainage limits.
    const clipToLog = (union, index) => {
      const exceedsLog = drawnPastLog(union.bounds.left, union.bounds.right, {
        padding, multiplier, startOffsetRD, length,
      });
      const clipRect = new Path.Rectangle(
        new Rectangle(new Point(padding, -100000), new Size(length, 200000))
      );
      const traceClip = new paper.Group([clipRect, union]);
      clipRect.clipMask = true;
      traceClip.name = "traceClip-" + index;
      if (exceedsLog) {
        clipAlerts.add(CLIP_ALERT, index);
      }
    };

    // The data-point marker for a named location sits on that location's
    // line at the point's own chainage.
    const namedLineY = {
      rightinvert: 0,
      rightspl: HWincl,
      centerline: HWincl + Arc,
      leftspl: HWincl + ArcFull,
      leftinvert: 2 * HWincl + ArcFull,
    };
    const markNamedPoint = (orientation, RD0, index) => {
      if (!(orientation in namedLineY)) return;
      const circle = new Path.Circle(
        new Point(chainageToCanvasX(RD0, padding, multiplier, startOffsetRD), Ymax - namedLineY[orientation]),
        2
      );
      circle.style = { fillColor: colors[index], strokeColor: colors[index], strokeWidth: 1 };
    };

    // Known chainages anchor at the centreline; the SPL chainages entered
    // alongside it are data points too.
    const markKnownChainages = (dimension, index) => {
      if ((dimension.orientationsource || "known") !== "chainages") return;
      markNamedPoint("leftspl", Number(dimension.p4), index);
      markNamedPoint("rightspl", Number(dimension.p2), index);
    };

    // The data-point marker, whichever way the trace was plotted. A face point
    // that lies on the tunnel surface is also dotted on the log, like a named one.
    const markDataPoint = (dimension, index, style) => {
      const orientation = dimension.orientation || "leftinvert";
      markKnownChainages(dimension, index);
      if (orientation !== "otherpoint") {
        markNamedPoint(orientation, Number(dimension.rd), index);
        return;
      }
      markFaceAndSurface(dimension, index, style, 0);
      // Face logs carry a known point on each face; both are data locations.
      const second = faceLogSecondPoint(dimension);
      if (second) markFaceAndSurface({ ...dimension, ...second }, index, style, 10);
    };

    const markFaceAndSurface = (dimension, index, style, labelLift) => {
      drawP6Point(dimension, index, style, discontinuityInputPoints, labelLift);
      const d = parseFloat(dimension.distancefromcenter) || 0;
      const ylog = perimeterYlog(
        dimension.tunnelside === "left" ? -d : d,
        parseFloat(dimension.verticalheight) || 0,
        geomM
      );
      if (ylog === null) return;
      const circle = new Path.Circle(
        new Point(chainageToCanvasX(dimension.rd, padding, multiplier, startOffsetRD), Ymax - ylog * multiplier),
        2
      );
      circle.style = { fillColor: colors[index], strokeColor: colors[index], strokeWidth: 1 };
    };

    props.dimensions.forEach((dimension, index) => {
      const orientation = dimension.orientation || "leftinvert";
      let style = {
        fillColor: "transparent",
        strokeColor: colors[index],
        strokeWidth: 1,
      };
      let rd;
      let traceunion = [];
      let D = parseInt(tracedimensions.direction);
      // The degenerate-case gates below test their angle for exactly 0, which
      // is only ever true of whole degrees, so they read the rounded pair.
      const DirRounded = parseInt(dimension.dir);

      // Everything past those gates plots from the unrounded pair: a computed
      // orientation keeps its exact figure, since rounding a bearing walks the
      // trace along the log.
      const exact = (v, fallback) => {
        const n = Number(v);
        return v === "" || v === null || v === undefined || !isFinite(n) ? fallback : n;
      };
      let amt = exact(dimension.amtExact, Number(dimension.amt));
      let Dir = exact(dimension.dirExact, DirRounded);

      // Preserve the user-entered values for the Input table (360 shown as 0).
      const DirInput = DirRounded === 360 ? 0 : DirRounded;
      const amtInput = parseInt(dimension.amt);

      // ---- Degenerate geometry (Algorithm Navigator 17Sep26, Routes A-C) ---
      // Every case where the main routine would divide by TanAlpha or TanBeta
      // is solved in the tunnel cross-section instead, before anything else
      // runs. The gates test an angle for exactly 0 or 90, which is only ever
      // true of whole degrees.
      const amtRounded = parseInt(dimension.amt);
      const inclinationDeg = parseFloat(tracedimensions.inclination) || 0;
      const Dwrap = D === 360 ? 0 : D;
      const Dirwrap = DirRounded === 360 ? 0 : DirRounded;
      const strikeParallel = alphaBetween(Dwrap, Dirwrap) === 0;
      const p6 = faceP6(dimension);

      // Lines at constant height spanning the whole log.
      const drawLevelTraces = (traces) => {
        const xStart = chainageToCanvasX(logStartRD, padding, multiplier, startOffsetRD);
        const xEnd = chainageToCanvasX(logEndRD, padding, multiplier, startOffsetRD);
        traces.forEach((t) => {
          const y = Ymax - t.Ylog * multiplier;
          const seg = new Path.Line(new Point(xStart, y), new Point(xEnd, y));
          seg.style = style;
        });
      };

      // Draw such traces and record them. No trace means the plane lies on
      // the log's own lines (or IS a wall) and the alert is the whole output.
      const finishParallel = ({ traces, alerts, alpha, adip, adipi, asf = 1 }) => {
        drawLevelTraces(traces);
        markDataPoint(dimension, index, style);
        alerts.filter(Boolean).forEach((a) => horizontalAlerts.add(a, index));
        usrvar["Alpha"] = alpha;
        usrvar["BetaHorizontal"] = adip;
        usrvar["Beta"] = adipi;
        usrvar["Inclination"] = inclinationDeg;
        usrvar["Span"] = "∞";
        usrvar["Tracelength"] = "∞";
        ["P1x", "P2x", "P3x", "P4x", "P5x"].forEach((k) => (usrvar[k] = "-"));
        inputRows.push(...buildInputRow(dimension, index, DirInput, amtInput));
        pushOutputRow(index, {
          alpha,
          adip,
          adipi,
          P1x: "-",
          P2x: "-",
          P3x: "-",
          P4x: "-",
          P5x: "-",
          span: "∞",
          // A trace on one of the log's own lines is not drawn again but still
          // runs the whole log; only a plane that is a wall leaves none.
          extent: traces.length || alerts.some((a) => LOG_LINE_ALERTS.includes(a))
            ? fullLogExtent(logStartRD, logEndRD)
            : NO_EXTENT,
          asf,
        });
        updateUserVariable(usrvar);
      };

      // Vertical plane with the strike parallel (Borderline Cases §1.1.3).
      if (amtRounded === 90 && strikeParallel) {
        const v = solveVertical({ orientation, geom: geomM, p6 });
        if (v) {
          finishParallel({
            traces: v.traces,
            alerts: [
              v.alert || ALPHA0_PARALLEL_NOTE,
              v.invertInterior && v.alert !== VERTICAL_CENTRELINE_ALERT ? ALPHA0_INVERT_ALERT : null,
            ],
            alpha: 0,
            adip: 0,
            adipi: 0,
          });
          return;
        }
      }

      // Level plane in a horizontal tunnel (Borderline Cases §1.1.1).
      if (amtRounded === 0 && inclinationDeg === 0) {
        const lv = solveLevel({ orientation, geom: geomM, p6 });
        if (lv) {
          finishParallel({
            traces: lv.alert ? [] : lv.traces,
            alerts: [lv.alert || FLAT_PARALLEL_NOTE],
            alpha: alphaBetween(Dwrap, Dirwrap),
            adip: 0,
            adipi: 0,
          });
          return;
        }
      }

      // Route A — strike parallel, dipping plane, horizontal tunnel.
      if (strikeParallel && inclinationDeg === 0) {
        const a0 = solveAlphaZero({ D: Dwrap, Dir: Dirwrap, amt: amtRounded, orientation, geom: geomM, p6 });
        if (a0) {
          finishParallel({
            traces: a0.alert ? [] : a0.traces,
            alerts: [
              a0.alert || (a0.lines ? `${ALPHA0_PARALLEL_NOTE} ${coincidenceAlert(a0.lines)}` : ALPHA0_PARALLEL_NOTE),
              !a0.alert && a0.invertInterior ? ALPHA0_INVERT_ALERT : null,
            ],
            alpha: 0,
            adip: 0,
            adipi: 0,
            // Asf = cos(90 - Amt): spacing of the traces across the tunnel.
            asf: Math.cos(toRadians(90 - amtRounded)),
          });
          return;
        }
      }

      // Route B — strike parallel, or a level plane, in an inclined tunnel.
      // The trace crosses the log once at the apparent dip I.
      if (inclinationDeg !== 0 && (strikeParallel || amtRounded === 0)) {
        const start = crossSectionPoint(orientation, geomM, p6);
        if (start) {
          const dopp = calculateDopp(Dwrap);
          const ai = alphaZeroInclined({
            Xp: start.Xp,
            Hp: start.Hp,
            S: transverseSign(Dwrap, Dirwrap) || 0,
            amt: amtRounded,
            I: inclinationDeg,
            RD0: Number(dimension.rd),
            geom: geomM,
          });
          const union = new Path({
            segments: ai.points.map((pt) => [
              chainageToCanvasX(pt.RD, padding, multiplier, startOffsetRD),
              Ymax - pt.Ylog * multiplier,
            ]),
            style,
          });
          markDataPoint(dimension, index, style);
          clipToLog(union, index);

          const alphaShown = amtRounded === 0 ? alphaBetween(Dwrap, Dirwrap) : 0;
          const extent = polylineExtent(ai.points);
          if (amtRounded === 0) horizontalAlerts.add(FLAT_INCLINED_ALERT, index);
          usrvar["Alpha"] = alphaShown;
          usrvar["BetaHorizontal"] = 0;
          usrvar["Beta"] = Math.abs(inclinationDeg);
          usrvar["Inclination"] = inclinationDeg;
          usrvar["ApDir"] = inclinationDeg > 0 ? dopp : Dwrap;
          usrvar["Span"] = fmt1(extent.max - extent.min);
          usrvar["Tracelength"] = polylineLength(ai.points);
          Object.keys(ai.crossings).forEach((k) => (usrvar[k + "x"] = ai.crossings[k].toFixed(2)));
          inputRows.push(...buildInputRow(dimension, index, DirInput, amtInput));
          pushOutputRow(index, {
            alpha: alphaShown,
            adip: 0,
            adipi: Math.abs(inclinationDeg),
            P1x: ai.crossings.P1,
            P2x: ai.crossings.P2,
            P3x: ai.crossings.P3,
            P4x: ai.crossings.P4,
            P5x: ai.crossings.P5,
            span: usrvar["Span"],
            extent: chainageExtent(extent.min, extent.max),
            asf: computeAsf({ amt: amtRounded, alpha: 0, inclination: inclinationDeg, apDirTrue: Dwrap, dopp }),
          });
          updateUserVariable(usrvar);
          return;
        }
      }

      // A 360° input means 0°.
      D = Dwrap;
      if (Dir === 360) Dir = 0;

      // Calculate Alpha using utility function
      let Alpha = calculateAlpha(D, Dir);
      let TanAlpha = Math.tan(toRadians(Alpha));
      let TanBeta = Math.tan(toRadians(amt)) * Math.sin(toRadians(Alpha));

      // Calculate quadrants
      let quadrants = calculateQuadrants(D);
      let { Q1min, Q1max, Q2min, Q2max, Q3min, Q3max, Q4min, Q4max } = quadrants;

      // Calculate Dopp and ApDir
      let Dopp = calculateDopp(D);
      let ApDir = calculateApDir(D, Dir, quadrants);
      // True apparent dip direction (horizontal reference) — the inclination
      // correction below may flip ApDir, but Asf needs the un-flipped one.
      const ApDirTrue = ApDir;

      // Apply Tunnel Inclination 'I' adjustment to Beta and ApDir.
      // I is signed: positive falls along D, negative rises.
      let I = parseFloat(tracedimensions.inclination) || 0;
      let BetaHorizontal = toDegree(Math.atan(TanBeta));
      let Beta = BetaHorizontal;
      if (I !== 0) {
        const corrected = correctApparentDip(Beta, ApDir, D, I);
        Beta = corrected.beta;
        ApDir = corrected.apDir;
        TanBeta = Math.tan(toRadians(Beta));
      }

      // Route C — the inclination correction has cancelled the apparent dip
      // along the axis: the plane contains the axis direction and the main
      // routine would divide by TanBeta = 0. Its trace is a line in the
      // cross-section whose intersections run the whole log.
      if (I !== 0 && Math.abs(Beta) < BETA_ZERO_TOLERANCE) {
        const pt = solveParallelTrace({ D, Dir, amt, alpha: Alpha, I, orientation, geom: geomM, p6 });
        if (pt) {
          finishParallel({
            traces: pt.alert ? [] : pt.traces,
            alerts: [pt.alert || AXIS_PARALLEL_NOTE, !pt.alert && pt.invertInterior ? ALPHA0_INVERT_ALERT : null],
            alpha: Alpha,
            adip: BetaHorizontal,
            adipi: 0,
            // Asf = sin(Gamma), Gamma the trace slope in the cross-section:
            // spacing measured across the tunnel, as in Route A.
            asf: Math.abs(pt.m) / Math.hypot(1, pt.m),
          });
          return;
        }
      }

      // Asf — Apparent Spacing Factor (Algorithm_Additional Xlog3D_Asf_10Aug26)
      const Asf = computeAsf({
        amt,
        alpha: Alpha,
        inclination: I,
        apDirTrue: ApDirTrue,
        dopp: Dopp,
      });
      usrvar["Asf"] = Asf;

      let H = [],
        XP = [],
        YP = [],
        XP5 = [],
        YP5 = [],
        XP3,
        XPmid,
        X2,
        Xmin,
        Xmax;

      if (!tracedimensions.iscrown || orientation !== "rightinvert") {
        rd = (dimension.rd - startOffsetRD) * multiplier;
      } else {
        if (ApDir === D) {
          rd = (dimension.rd - startOffsetRD) * multiplier + hw / TanBeta;
        } else {
          rd = (dimension.rd - startOffsetRD) * multiplier - hw / TanBeta;
        }
      }

      let X1 = padding + rd;
      let P1 = new Point(X1, Ymax);
      let Diff = D - Dir;

      // Calculate XP3 and XPmid based on quadrants (existing logic). The
      // quadrants give the level-tunnel ApDir, which fixes the side the trace
      // crosses to; the inclination-corrected ApDir (which a folded dip turns
      // over) only sets the lean up the walls and crown.
      let sideApDir = ApDirTrue;
      if (Dir == D) {
        XP3 = X1;
        XPmid = X1;
        sideApDir = D;
      }

      if (Dir == Dopp) {
        XP3 = X1;
        XPmid = X1;
        sideApDir = Dopp;
      }

      if ((Q1min < Dir && Dir < (Q1min + 90)) || (Dir < Q1max && Dir > (D - 90))) {
        XP3 = P1.x - (width / TanAlpha);
        XPmid = P1.x - Math.abs((P1.x - XP3) / 2);
        sideApDir = D;
      }

      if ((Q2min < Dir && Dir < (Q2min + 90)) || (Dir < Q2max && Dir > (Q2max - 90))) {
        XP3 = P1.x + (width / TanAlpha);
        XPmid = P1.x + Math.abs((P1.x - XP3) / 2);
        sideApDir = D;
      }

      if ((Q3min < Dir && Dir < (Q3min + 90)) || (Dir < Q3max && Dir > (Q3max - 90))) {
        XP3 = P1.x - (width / TanAlpha);
        XPmid = P1.x - Math.abs((P1.x - XP3) / 2);
        sideApDir = Dopp;
      }

      if ((Q4min < Dir && Dir < (Q4min + 90)) || (Dir < Q4max && Dir > (Q4max - 90))) {
        XP3 = P1.x - (width / TanAlpha);
        XPmid = P1.x - Math.abs((P1.x - XP3) / 2);
        sideApDir = Dopp;
      }

      usrvar["RD"] = rd / multiplier;
      usrvar["AMT"] = amt;
      usrvar["Alpha"] = Alpha;
      usrvar["TanAlpha"] = TanAlpha;
      usrvar["Inclination"] = I;
      usrvar["BetaHorizontal"] = BetaHorizontal;
      usrvar["Beta"] = Beta;
      usrvar["TanBeta"] = TanBeta;
      usrvar["HWn"] = HWn / multiplier;
      usrvar["Ymax"] = (Ymax - padding) / multiplier;
      usrvar["ApDir"] = ApDir;

      let Dist = 0,
        XP2a,
        XP4a;

      // The SPL corner sits (WS - W)/2 outside the invert edge, whichever way
      // the plane dips; the rise to it is the wall height hw (HWincl is only
      // its unfolded length on the log).
      Dist = (ws - width) / 2 / TanAlpha;
      if (sideApDir === D) {
        if ((Diff > -360 && Diff < -270) || (Diff > 0 && Diff < 90)) {
          XP3 = X1 - width / TanAlpha;
          XPmid = X1 - Math.abs(X1 - XP3) / 2;
          XP2a = X1 + Dist;
          XP4a = XP3 - Dist;
        } else {
          XP3 = X1 + width / TanAlpha;
          XPmid = X1 + Math.abs(X1 - XP3) / 2;
          XP2a = X1 - Dist;
          XP4a = XP3 + Dist;
        }
      } else {
        Diff = Dopp - Dir;
        if ((Diff > -360 && Diff < -270) || (Diff > 0 && Diff < 90)) {
          XP3 = X1 - width / TanAlpha;
          XPmid = X1 - Math.abs(X1 - XP3) / 2;
          XP2a = X1 + Dist;
          XP4a = XP3 - Dist;
        } else {
          XP3 = X1 + width / TanAlpha;
          XPmid = X1 + Math.abs(X1 - XP3) / 2;
          XP2a = X1 - Dist;
          XP4a = XP3 + Dist;
        }
      }
      X2 = ApDir === D ? XP2a - hw / TanBeta : XP2a + hw / TanBeta;

      let P2 = new Point(X2, Ymax - HWincl);
      let P3 = new Point(XP3, Ymax - (2 * HWincl + ArcFull));
      let P4 = new Point(XP4a - hw / TanBeta, Ymax - (HWincl + ArcFull));
      if (ApDir !== D) {
        P4 = new Point(XP4a + hw / TanBeta, Ymax - (HWincl + ArcFull));
      }
      let P5;

      if (!tracedimensions.iscrown) {
        if (Xmin) {
          Xmin = Math.min(Xmin, P1.x, P2.x);
        } else {
          Xmin = Math.min(P1.x, P2.x);
        }
        if (Xmax) {
          Xmax = Math.max(Xmax, P1.x, P2.x);
        } else {
          Xmax = Math.max(P1.x, P2.x);
        }
        traceunion.push([P1.x, P1.y]);
        traceunion.push([P2.x, P2.y]);
      }

      scalearr = [];
      // Chainage extent of the trace over the crown, from SPL to SPL.

      // Right crown
      for (let n = Theta; n <= 90; n++) {
        H[n] = r * Math.sin(toRadians(n));
        if (ApDir === D) {
          if (P2.x > P4.x) {
            XP[n] = XP2a - Math.abs(XP2a - XPmid) * ((n - Theta) / (90 - Theta));
          } else {
            XP[n] = XP2a + Math.abs(XP2a - XPmid) * ((n - Theta) / (90 - Theta));
          }
          XP5[n] = XP[n] - (H[n] + HWn) / TanBeta;
        } else {
          if (P2.x > P4.x) {
            XP[n] = XP2a - Math.abs(XPmid - XP2a) * ((n - Theta) / (90 - Theta));
          } else {
            XP[n] = XP2a + Math.abs(XPmid - XP2a) * ((n - Theta) / (90 - Theta));
          }
          XP5[n] = XP[n] + (H[n] + HWn) / TanBeta;
        }
        YP[n] = Ymax - (HWincl + Arc * ((n - Theta) / (90 - Theta)));
        YP5[n] = YP[n];
        scalearr.push([XP5[n], YP5[n]]);
        if (Xmin) {
          Xmin = Math.min(Xmin, XP5[n]);
        } else {
          Xmin = XP5[n];
        }
        if (Xmax) {
          Xmax = Math.max(Xmax, XP5[n]);
        } else {
          Xmax = XP5[n];
        }
        traceunion.push([XP5[n], YP5[n]]);
        usrvar["Prc" + n] = "(" + (XP5[n] - padding) / multiplier + "," + (HWincl + Arc * ((n - Theta) / (90 - Theta))) / multiplier + ")";
      }

      let rcrown = new Path({
        segments: scalearr,
      });

      // Left crown
      scalearr = [];
      H = [];
      XP = [];
      YP = [];
      XP5 = [];
      YP5 = [];
      for (let n = 90; n >= Theta; n--) {
        H[n] = r * Math.sin(toRadians(n));
        if (parseInt(ApDir) === D) {
          if (P2.x > P4.x) {
            XP[n] = XP4a + Math.abs(XP4a - XPmid) * ((n - Theta) / (90 - Theta));
          } else {
            XP[n] = XP4a - Math.abs(XP4a - XPmid) * ((n - Theta) / (90 - Theta));
          }
          XP5[n] = XP[n] - (H[n] + HWn) / TanBeta;
        } else {
          if (P2.x > P4.x) {
            XP[n] = XP4a + Math.abs(XP4a - XPmid) * ((n - Theta) / (90 - Theta));
          } else {
            XP[n] = XP4a - Math.abs(XP4a - XPmid) * ((n - Theta) / (90 - Theta));
          }
          XP5[n] = XP[n] + (H[n] + HWn) / TanBeta;
        }
        YP[n] = Ymax - (HWincl + ArcFull - Arc * ((n - Theta) / (90 - Theta)));
        YP5[n] = YP[n];
        scalearr.push([XP5[n], YP5[n]]);
        if (Xmin) {
          Xmin = Math.min(Xmin, XP5[n]);
        } else {
          Xmin = XP5[n];
        }
        if (Xmax) {
          Xmax = Math.max(Xmax, XP5[n]);
        } else {
          Xmax = XP5[n];
        }
        traceunion.push([XP5[n], YP5[n]]);
        if (n === 90) {
          P5 = new Point(XP5[n], YP5[n]);
        }
        usrvar["Plc" + n] = "(" + (XP5[n] - padding) / multiplier + "," + (HWincl + ArcFull - Arc * ((n - Theta) / (90 - Theta))) / multiplier + ")";
      }

      let lcrown = new Path({
        segments: scalearr,
      });

      // The crown samples step in whole degrees and can stop short of the
      // SPL; the trace's own SPL crossings close the crown span.

      if (!tracedimensions.iscrown) {
        if (Xmin) {
          Xmin = Math.min(Xmin, P4.x, P3.x);
        } else {
          Xmin = Math.min(P4.x, P3.x);
        }
        if (Xmax) {
          Xmax = Math.max(Xmax, P4.x, P3.x);
        } else {
          Xmax = Math.max(P4.x, P3.x);
        }
        traceunion.push([P4.x, P4.y]);
        traceunion.push([P3.x, P3.y]);
      }

      let union = new Path({
        segments: traceunion,
        style: style,
      });

      if (!tracedimensions.iscrown) {
        let tracelength = union["length"] / multiplier;
        usrvar["Tracelength"] = tracelength;
        usrvar["Tracecrown"] = (rcrown["length"] + lcrown["length"]) / multiplier;
      } else {
        let tracelength = union["length"] / multiplier;
        usrvar["Tracelength"] = tracelength;
        usrvar["Tracecrown"] = tracelength;
      }

      // Handle orientation positioning
      usrvar["XPmid"] = (XPmid - padding) / multiplier;
      let diff;

      if (orientation === "centerline") {
        diff = P5.x - P1.x;
        let pointx = union.position.x - diff;
        let pointy = union.position.y;
        union.position = new Point(pointx, pointy);
        let circle = new Path.Circle(new Point(P1.x, P5.y), 2);
        circle.style = style;
        circle.fillColor = colors[index];
      }

      if (orientation === "leftinvert") {
        diff = P3.x - P1.x;
        let pointx = union.position.x - diff;
        let pointy = union.position.y;
        union.position = new Point(pointx, pointy);
        let circle = new Path.Circle(new Point(P1.x, P3.y), 2);
        circle.style = style;
        circle.fillColor = colors[index];
      }

      if (orientation === "leftspl") {
        diff = P4.x - P1.x;
        let pointx = union.position.x - diff;
        let pointy = union.position.y;
        union.position = new Point(pointx, pointy);
        let circle = new Path.Circle(new Point(P1.x, P4.y), 2);
        circle.style = style;
        circle.fillColor = colors[index];
      }

      if (orientation === "rightspl") {
        diff = P2.x - P1.x;
        let pointx = union.position.x - diff;
        let pointy = union.position.y;
        union.position = new Point(pointx, pointy);
        let circle = new Path.Circle(new Point(P1.x, P2.y), 2);
        circle.style = style;
        circle.fillColor = colors[index];
      }

      markKnownChainages(dimension, index);
      // P6 — "Any Other Point" on the face: side, offset d and height Hp above
      // the invert. The trace was built with P1 at the entered chainage. Across
      // the tunnel the plane moves along the log by (XP3 - X1) per invert width,
      // and upwards by (X2 - XP2a) per wall height, so it reaches the point at
      // x6; the whole trace moves by the difference so it passes through it.
      // Unlike sliding along the drawn log, this holds for points inside the
      // face as well as on its surface.
      if (orientation === "otherpoint") {
        const d = parseFloat(dimension.distancefromcenter) || 0;
        const Xp = (dimension.tunnelside === "left" ? -d : d) * multiplier;
        const Hp = (parseFloat(dimension.verticalheight) || 0) * multiplier;
        const perAcross = (XP3 - X1) / -width;
        const perUp = (X2 - XP2a) / hw;
        const x6 = X1 + perAcross * (Xp - width / 2) + perUp * Hp;
        diff = x6 - X1;
        union.position = new Point(union.position.x - diff, union.position.y);

        // Draw the tunnel face line, text, arrows and P6 point on legend
        markDataPoint(dimension, index, style);
      }

      if (orientation === "rightinvert") {
        diff = 0;
        let circle = new Path.Circle(new Point(P1.x, P1.y), 2);
        circle.style = style;
        circle.fillColor = colors[index];
      }

      clipToLog(union, index);

      // Display trace info
      usrvar["P1"] = "(" + ((P1.x - (diff || 0) - padding) / multiplier + startOffsetRD).toFixed(2) + ",0)";
      usrvar["P2"] = "(" + ((P2.x - (diff || 0) - padding) / multiplier + startOffsetRD).toFixed(2) + "," + (hw / multiplier).toFixed(2) + ")";
      usrvar["P3"] = "(" + ((P3.x - (diff || 0) - padding) / multiplier + startOffsetRD).toFixed(2) + "," + ((2 * hw + ArcFull) / multiplier).toFixed(2) + ")";
      usrvar["P4"] = "(" + ((P4.x - (diff || 0) - padding) / multiplier + startOffsetRD).toFixed(2) + "," + ((hw + ArcFull) / multiplier).toFixed(2) + ")";
      usrvar["P5"] = "(" + ((P5.x - (diff || 0) - padding) / multiplier + startOffsetRD).toFixed(2) + "," + ((hw + Arc) / multiplier).toFixed(2) + ")";

      // Extract X-only chainages for Ch. output
      const extractX = (pStr) => pStr ? pStr.slice(1, pStr.indexOf(",")) : "-";
      usrvar["P1x"] = extractX(usrvar["P1"]);
      usrvar["P2x"] = extractX(usrvar["P2"]);
      usrvar["P3x"] = extractX(usrvar["P3"]);
      usrvar["P4x"] = extractX(usrvar["P4"]);
      usrvar["P5x"] = extractX(usrvar["P5"]);

      // Beta was inclination-adjusted earlier in this scope.
      const Span = ((Xmax - Xmin) / multiplier).toFixed(1).replace(/\.0$/, "");

      inputRows.push(...buildInputRow(dimension, index, DirInput, amtInput));
      pushOutputRow(index, {
        alpha: Alpha,
        adip: BetaHorizontal,
        adipi: Beta,
        P1x: usrvar["P1x"],
        P2x: usrvar["P2x"],
        P3x: usrvar["P3x"],
        P4x: usrvar["P4x"],
        P5x: usrvar["P5x"],
        span: Span,
        extent: chainageExtent(
          (Xmin - (diff || 0) - padding) / multiplier + startOffsetRD,
          (Xmax - (diff || 0) - padding) / multiplier + startOffsetRD
        ),
        asf: Asf,
      });

      updateUserVariable(usrvar);
    });

    // Raise each distinct alert once, naming the traces that raised it. Only
    // new or edited traces pop up; the rest are already on the printed list.
    // A redraw of the same traces keeps the pop-ups already showing; any
    // change drops them (and any left over from the input page) first.
    const popUp = changedTraces("horseshoe", props.dimensions);
    if (popUp.size) removeAllToasts();
    const clipPopUps = clipAlerts.messages(popUp);
    if (clipPopUps.length && window.audio) window.audio.play();
    clipPopUps.forEach((message) => {
      addToast(message, { appearance: "info" });
    });
    horizontalAlerts.messages(popUp).forEach((message) => {
      addToast(message, { appearance: "warning" });
    });

    // Draw discontinuity input location label
    if (fixedTextPosition && discontinuityInputPoints.length > 0) {
      const linepath2centerText = new PointText(fixedTextPosition);
      linepath2centerText.content = "Discontinuity data location";
      linepath2centerText.style = { fontSize: 8 };
      linepath2centerText.fillColor = "black";

      discontinuityInputPoints.forEach(({ point, style, color }) => {
        const linepath2center = new Path.Line(point, fixedTextPosition);
        linepath2center.style = { ...style, dashArray: [3, 3] };
        linepath2center.strokeColor = color;
      });
    }

    // ---- Tabulated Inputs / Outputs (per 17May26 Trace Page spec) ----
    const drawBorder = (x1, y1, x2, y2) => {
      const p = new Path.Line(new Point(x1, y1), new Point(x2, y2));
      p.strokeColor = "black";
      p.strokeWidth = 0.5;
      return p;
    };
    const cellText = (txt, xL, xR, yT, yB, opts = {}) => {
      const t = new PointText(new Point((xL + xR) / 2, (yT + yB) / 2 + 3));
      t.justification = "center";
      t.fillColor = opts.color || "black";
      if (opts.bold) t.fontWeight = "bold";
      t.fontSize = opts.fontSize || 10;
      t.content = txt;
      return t;
    };
    const buildCols = (xStart, defs) => {
      let x = xStart;
      return defs.map((d) => {
        const col = { ...d, left: x, right: x + d.width };
        x += d.width;
        return col;
      });
    };
    const noteLine = (yPos, txt, opts = {}) => {
      const n = new PointText(new Point(padding + (opts.indent || 0), yPos));
      n.justification = "left";
      n.fillColor = "black";
      if (opts.bold) n.fontWeight = "bold";
      n.fontSize = opts.fontSize || 9;
      n.content = txt;
      return n;
    };

    let curY = points + padding + 2 * (hw + Arc);

    // ----- Inputs section -----
    const inLabel = new PointText(new Point(padding, curY));
    inLabel.justification = "left";
    inLabel.fillColor = "black";
    inLabel.fontWeight = "bold";
    inLabel.content = "Inputs:";
    curY += 18;
    const geomText = new PointText(new Point(padding, curY));
    geomText.justification = "left";
    geomText.fillColor = "black";
    const I0 = parseFloat(tracedimensions.inclination) || 0;
    const D0 = parseInt(tracedimensions.direction);
    geomText.content =
      "Tunnel Direction: " + D0 +
      "; Tunnel Inclination: " + I0 +
      "; Height: " + (height / multiplier) +
      "; Width at Invert: " + (width / multiplier) +
      "; Width at SPL: " + (ws / multiplier) +
      "; Wall Height: " + (hw / multiplier);
    curY += 20;

    // Input table
    const inColDefs = [
      { key: "no", label: "D.No.", width: 45 },
      { key: "label", label: "Label", width: 45 },
      { key: "dir", label: "DipDir", width: 50 },
      { key: "amt", label: "DipAmt", width: 50 },
      { key: "ch", label: "Chainage", width: 60 },
      { key: "surface", label: "Tunnel Surface", width: 90 },
      { key: "side", label: "Side", width: 45 },
      { key: "dist", label: "Distance", width: 60 },
      { key: "ht", label: "Height", width: 50 },
    ];
    // With every trace sourced from known chainages there is no face heading to
    // report, so the last three columns carry the chainages that were entered.
    const allFromChainages =
      inputRows.length > 0 && inputRows.every((r) => r.source === "chainages");
    const colIdx = (key) => inColDefs.findIndex((c) => c.key === key);
    if (allFromChainages) {
      Object.assign(inColDefs[colIdx("side")], { label: "Ch. Left SPL", width: 65 });
      Object.assign(inColDefs[colIdx("dist")], { label: "Ch. Right SPL", width: 70 });
      Object.assign(inColDefs[colIdx("ht")], { label: "Ch. Centreline", width: 75 });
    }
    const inCols = buildCols(padding, inColDefs);
    const inGroupStartIdx = colIdx("ch"); // "Discontinuity Data Location" group
    const inFaceStartIdx = colIdx("side"); // "Face (Heading)" sub-group
    const inLeftX = inCols[0].left;
    const inRightX = inCols[inCols.length - 1].right;
    const inHeaderRowH = 18;
    const inDataRowH = 18;
    const inHeaderY1 = curY;
    const inHeaderY2 = inHeaderY1 + inHeaderRowH;
    const inHeaderY3 = inHeaderY2 + inHeaderRowH;
    const inDataY0 = inHeaderY3 + inHeaderRowH;
    const inTableBottomY = inDataY0 + inDataRowH * inputRows.length;

    inCols.forEach((col, i) => {
      if (i < inGroupStartIdx) {
        cellText(col.label, col.left, col.right, inHeaderY1, inDataY0, { bold: true });
      }
    });
    cellText(
      "Discontinuity Data Location",
      inCols[inGroupStartIdx].left,
      inRightX,
      inHeaderY1,
      inHeaderY2,
      { bold: true }
    );
    inCols.forEach((col, i) => {
      if (i >= inGroupStartIdx && i < inFaceStartIdx) {
        cellText(col.label, col.left, col.right, inHeaderY2, inDataY0, { bold: true });
      }
    });
    cellText(
      allFromChainages ? "Known Trace Chainages (m)" : "Face (Heading)",
      inCols[inFaceStartIdx].left,
      inRightX,
      inHeaderY2,
      inHeaderY3,
      { bold: true }
    );
    inCols.forEach((col, i) => {
      if (i >= inFaceStartIdx) {
        cellText(col.label, col.left, col.right, inHeaderY3, inDataY0, { bold: true });
      }
    });
    inputRows.forEach((row, r) => {
      const yT = inDataY0 + r * inDataRowH;
      const yB = yT + inDataRowH;
      inCols.forEach((col) => {
        cellText(row[col.key] || "-", col.left, col.right, yT, yB, { color: row.color });
      });
    });
    drawBorder(inLeftX, inHeaderY1, inRightX, inHeaderY1);
    drawBorder(inLeftX, inTableBottomY, inRightX, inTableBottomY);
    drawBorder(inLeftX, inHeaderY1, inLeftX, inTableBottomY);
    drawBorder(inRightX, inHeaderY1, inRightX, inTableBottomY);
    inCols.forEach((col, i) => {
      if (i === 0) return;
      let startY = inHeaderY1;
      if (i > inFaceStartIdx) startY = inHeaderY3;
      else if (i > inGroupStartIdx) startY = inHeaderY2;
      drawBorder(col.left, startY, col.left, inTableBottomY);
    });
    drawBorder(inCols[inGroupStartIdx].left, inHeaderY2, inRightX, inHeaderY2);
    drawBorder(inCols[inFaceStartIdx].left, inHeaderY3, inRightX, inHeaderY3);
    drawBorder(inLeftX, inDataY0, inRightX, inDataY0);
    for (let r = 1; r < inputRows.length; r++) {
      const y = inDataY0 + r * inDataRowH;
      drawBorder(inLeftX, y, inRightX, y);
    }
    curY = inTableBottomY + 14;

    const inputNotesLabel = noteLine(curY, "Notes:", { bold: true });
    noteLine(curY, " D.No. (Discontinuity number); Label (Discontinuity label); DipDir (Dip direction); DipAmt (Dip amount); Chainage (RD); Tunnel Surface", {
      indent: inputNotesLabel.bounds.width + 2,
    });
    curY += 12;
    const inputNotesEnd = noteLine(
      curY,
      allFromChainages
        ? "(data point location on tunnel surface); Known Trace Chainages: the chainages entered for the trace at left SPL, right SPL and the centreline."
        : "(data point location on tunnel surface); Face (Heading): Side (wrt tunnel centreline), Distance (from centreline), Height (from invert)."
    );
    // Key to the dot that marks each data point on the log.
    const dataPointKeyX = inputNotesEnd.bounds.right + 10;
    const dataPointKeyDot = new Path.Circle(new Point(dataPointKeyX, curY - 3), 2);
    dataPointKeyDot.style = { fillColor: "black", strokeColor: "black", strokeWidth: 1 };
    noteLine(curY, "Data Point", { indent: dataPointKeyX + 5 - padding });
    curY += 22;

    // ----- Outputs section -----
    const outLabel = new PointText(new Point(padding, curY));
    outLabel.justification = "left";
    outLabel.fillColor = "black";
    outLabel.fontWeight = "bold";
    outLabel.content = "Outputs:";
    curY += 18;
    const crownText = new PointText(new Point(padding, curY));
    crownText.justification = "left";
    crownText.fillColor = "black";
    crownText.content =
      "Crown radius: " + (r / multiplier).toFixed(1).replace(/\.0$/, "") +
      "; Crown circumference: " + (ArcFull / multiplier).toFixed(1).replace(/\.0$/, "");
    curY += 20;

    const outColDefs = [
      { key: "no", label: "D.No.", width: 42 },
      { key: "angle", label: "Angle", width: 45 },
      { key: "adip", label: "ADip", width: 45 },
      { key: "adipi", label: "ADipI", width: 50 },
      { key: "chli", label: "ChLI", width: 45 },
      { key: "chri", label: "ChRI", width: 45 },
      { key: "chlspl", label: "ChLSPL", width: 55 },
      { key: "chrspl", label: "ChRSPL", width: 55 },
      { key: "chcl", label: "ChCL", width: 50 },
      { key: "chmin", label: "ChMin", width: 50 },
      { key: "chmax", label: "ChMax", width: 50 },
      { key: "tsp", label: "TSp", width: 45 },
      { key: "asf", label: "Asf", width: 45 },
    ];
    const outCols = buildCols(padding, outColDefs);
    const outLeftX = outCols[0].left;
    const outRightX = outCols[outCols.length - 1].right;
    const outHeaderRowH = 18;
    const outDataRowH = 18;
    const outHeaderY = curY;
    const outDataY0 = outHeaderY + outHeaderRowH;
    const outTableBottomY = outDataY0 + outDataRowH * outputRows.length;

    outCols.forEach((col) => {
      cellText(col.label, col.left, col.right, outHeaderY, outDataY0, { bold: true });
    });
    outputRows.forEach((row, r) => {
      const yT = outDataY0 + r * outDataRowH;
      const yB = yT + outDataRowH;
      outCols.forEach((col) => {
        const v = row[col.key];
        cellText(v != null ? String(v) : "-", col.left, col.right, yT, yB, { color: row.color });
      });
    });
    drawBorder(outLeftX, outHeaderY, outRightX, outHeaderY);
    drawBorder(outLeftX, outTableBottomY, outRightX, outTableBottomY);
    drawBorder(outLeftX, outHeaderY, outLeftX, outTableBottomY);
    drawBorder(outRightX, outHeaderY, outRightX, outTableBottomY);
    outCols.forEach((col, i) => {
      if (i === 0) return;
      drawBorder(col.left, outHeaderY, col.left, outTableBottomY);
    });
    drawBorder(outLeftX, outDataY0, outRightX, outDataY0);
    for (let r = 1; r < outputRows.length; r++) {
      const y = outDataY0 + r * outDataRowH;
      drawBorder(outLeftX, y, outRightX, y);
    }
    curY = outTableBottomY + 14;

    // The persistence caution reads first, directly under the Outputs table.
    const cautionLabel = noteLine(curY, "Caution:", { bold: true });
    noteLine(curY, " Discontinuity trace span may be evaluated with care in consideration of its persistence.", {
      indent: cautionLabel.bounds.width + 2,
    });
    curY += 16;

    const outputAlerts = [...clipAlerts.messages(), ...horizontalAlerts.messages()];
    if (outputAlerts.length) {
      noteLine(curY, "Alerts:", { bold: true });
      curY += 14;
      outputAlerts.forEach((message) => {
        let line = noteLine(curY, "");
        let words = "";
        message.split(/\s+/).forEach((word) => {
          const candidate = words ? words + " " + word : word;
          line.content = candidate;
          if (line.bounds.width > outRightX - padding && words) {
            line.content = words;
            curY += 12;
            line = noteLine(curY, word);
            words = word;
          } else {
            words = candidate;
          }
        });
        curY += 16;
      });
    }

    // Output abbreviations. Labelled "Abbreviations used" rather than "Notes"
    // to separate it from the notes proper below — the Asf specification calls
    // these two things by those names.
    const outputNotesLabel = noteLine(curY, "Abbreviations used:", { bold: true });
    noteLine(curY, " Angle (Angle between strike and tunnel direction); ADip (Apparent Dip with horizontal);", {
      indent: outputNotesLabel.bounds.width + 2,
    });
    curY += 12;
    noteLine(curY, "ADipI (Apparent Dip with tunnel axis); Trace chainage: ChLI (left invert), ChRI (right invert), ChLSPL (left SPL),");
    curY += 12;
    noteLine(curY, "ChRSPL (right SPL), ChCL (centreline), ChMin (minimum of trace), ChMax (maximum of trace); TSp: Trace span along tunnel;");
    curY += 12;
    noteLine(curY, "Asf: Apparent Spacing Factor.");
    curY += 16;

    // Asf usage notes (Algorithm_Additional Xlog3D_Asf_10Aug26)
    noteLine(curY, "•  Divide True Spacing by the Apparent Spacing Factor Asf to get Apparent Spacing along the tunnel.");
    curY += 12;
    noteLine(curY, "•  True Spacing is the distance between two adjoining discontinuities of the same set across their dip planes. This is");
    curY += 12;
    noteLine(curY, "the shortest distance between the two.", { indent: 11 });
    curY += 12;
    noteLine(curY, "•  Apply Asf for computation of Apparent Thickness and Apparent Aperture along the tunnel.");
    curY += 12;
    // The last note carries a link to the TrueSpace tool. The notes are drawn
    // on the canvas, so this is a canvas item rather than an anchor: blue
    // underlined text, with a rectangle over it as the click target.
    const trueSpaceNote = noteLine(
      curY,
      "•  If True Spacing not available, get True Spacing from this app: "
    );
    const trueSpaceLink = noteLine(curY, TRUESPACE_LABEL, {
      indent: trueSpaceNote.bounds.width + 3,
    });
    trueSpaceLink.fillColor = "#0b57d0";
    const linkUnderline = new Path.Line(
      trueSpaceLink.bounds.bottomLeft.add([0, 1]),
      trueSpaceLink.bounds.bottomRight.add([0, 1])
    );
    linkUnderline.strokeColor = "#0b57d0";
    linkUnderline.strokeWidth = 0.6;
    // Handled by the canvas element rather than a paper item — see
    // projectPointFromEvent for why paper's own hit-testing is off by the
    // device pixel ratio here.
    const box = trueSpaceLink.bounds.expand(6);
    trueSpaceLinkBounds.current = {
      left: box.left,
      right: box.right,
      top: box.top,
      bottom: box.bottom,
    };
    curY += 16;

    // The notes now run past the height creategraph reserved: grow to fit.
    paper.view.viewSize = new Size(paper.view.viewSize.width, Math.max(paper.view.viewSize.height, curY + 20));
  };

  const draw = (event) => {
    createScale();
    createLog();
    createTrace();
  };

  const creategraph = () => {
    paper.project.clear();
    let graphWidth = Math.max(length + 450, padding + 700);
    let graphHeight = Math.max(
      props.dimensions.length * 75 + 400,
      padding + 2 * (hw + Arc) + 350 + props.dimensions.length * 40
    );
    paper.view.viewSize = new Size(graphWidth, graphHeight);
    draw();
    paper.view.draw();
  };

  const addTrace = () => {
    props.addtrace();
  };

  const showDeleteTrace = () => {
    setShow(true);
  };

  const deleteTrace = () => {
    props.deletetrace(selectedTrace);
    setShow(false);
  };

  const selectTrace = (e) => {
    let value = e.target.value;
    setSelectedTrace(value);
  };

  return (
    <div id="page">
      <Header onBack={props.showinput} />
      <Footer />
      <div className="page-content header-clear-medium">
        <div className="row">
          <div className="col-md-12">
            <button
              onClick={() => {
                props.showinput();
              }}
              className="btn bg-green-dark mw-2 me-3"
            >
              Edit Input
            </button>
            <button onClick={addTrace} className="btn bg-green-dark me-3">
              Add Trace
            </button>
            {props.tracecount > 0 ? (
              <button onClick={showDeleteTrace} className="btn bg-danger me-3">
                Delete Trace
              </button>
            ) : (
              ""
            )}
            <DownloadMenu filename="horseshoe-plot" screenLabel="Horseshoe Tunnel — Plot" feedbackInputs={props.dimensions} />
          </div>
          <div className="col-md-6"></div>
          <div className="col-md-12 overflow-auto">{!props.showdebug ? <canvas id="paper-canvas" resize="true" onClick={handleCanvasClick} onMouseMove={handleCanvasMouseMove} /> : <Debug uservariables={uservariables} />}</div>
        </div>
      </div>
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Select trace to delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {props.dimensions.map((dimension, index) => (
            <Form.Check onChange={selectTrace} inline value={index} style={{ color: colors[index] }} label={"Trace " + (index + 1)} name="group1" type="radio" id={index} key={index} />
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          <Button variant="danger" onClick={deleteTrace}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Output;
